//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { LocalizationHelper, TypeHelper } from '../../../../helpers'
import { GridOptions, GridController, GridTypes } from '../../grid/grid.controller'
import { TreeListOptions } from '../../tree-list/tree-list.controller'

import { IIMRSelectGrid, IMRSelectGridOptions } from './select-grid.interfaces'
import { IMRBaseControlController } from '../../base'

export class IMRSelectGridController<TObject>
    extends
    IMRBaseControlController<IIMRSelectGrid<TObject>, IMRSelectGridOptions<TObject>>
    implements
    IIMRSelectGrid<TObject> {

    //#region Members

    public isBottomToolbarVisible: boolean

    private gridController: GridController<TObject>
    public gridOptions: GridOptions

    private treeListController: any
    public treeListOptions: TreeListOptions

    private submitButton: DevExpress.ui.dxButton
    public bottomToolbarOptions: DevExpress.ui.dxToolbarOptions

    private isMultiSelect: boolean
    private isRequired: boolean
    private isShowSelectButton: boolean
    private isShowCancelButton: boolean
    private isClearSelectionAfterSelect: boolean

    //#endregion


    //#region Ctor

    public constructor() {
        super()
    }

    //#endregion


    //#region IIMRSelectGrid Implementation

    public getGrid(): GridController<TObject> {
        return this.gridController
    }

    public getTreeList(): any {
        return this.treeListController
    }

    public reset(): void {
        if (this.gridController != null) {
            this.gridController.dxGrid.clearSelection()
        }

        if (this.treeListController != null) {
            this.treeListController.table.clearSelection()
        }
    }

    //#endregion


    //#region Events

    private onObjectsSelected(objects: TObject[]): void {
        if (this.isMultiSelect) {
            super.callEvent(this.options.onObjectsSelected, {
                selectedObjects: objects
            })
        } else {
            super.callEvent(this.options.onObjectSelected, {
                selectedObject: _.first(objects) || null
            })
        }
    }

    private onSelectionChanged(objects: TObject[]): void {
        super.callEvent(this.options.onSelectionChanged, {
            selectedObjects: objects
        })
    }

    //#endregion


    //#region Lifecycle

    protected initialize(): JQueryPromise<void> {
        this.setDefaults()
        this.setOptions()

        return $
            .when(
                this.initializeTable(),
                this.initializeBottomToolbar()
            )
    }

    private setDefaults(): void {
        this.gridOptions = null
        this.isClearSelectionAfterSelect = false
        this.isMultiSelect = false
        this.isRequired = false
        this.isShowSelectButton = true
        this.isShowCancelButton = false
        this.treeListOptions = null
    }

    private setOptions(): void {
        if (this.options != null) {
            if (this.options.gridOptions != null) {
                this.gridOptions = TypeHelper.clone(this.options.gridOptions, true)
            }

            if (this.options.treeListOptions != null) {
                this.treeListOptions = TypeHelper.clone(this.options.treeListOptions, true)
            }

            if (this.options.multiSelect != null) {
                this.isMultiSelect = this.options.multiSelect
            }

            if (this.options.required != null) {
                this.isRequired = this.options.required
            }

            if (this.options.showSelectButton != null) {
                this.isShowSelectButton = this.options.showSelectButton
            }

            if (this.options.showCancelButton != null) {
                this.isShowCancelButton = this.options.showCancelButton
            }

            if (this.options.clearSelectionAfterSelect != null) {
                this.isClearSelectionAfterSelect = this.options.clearSelectionAfterSelect
            }
        }
    }

    //#endregion


    //#region Common Grid/Tree

    private initializeTable(): JQueryPromise<any> {
        if (this.gridOptions != null) {
            return this.initializeGrid()
        }
        else if (this.treeListOptions != null) {
            return this.initializeTree()
        }
    }

    private overwriteTableOptions<T extends GridOptions | TreeListOptions>(options: T): T {
        if (options == null) {
            options = {} as T
        }

        options.onRowDoubleClick = TypeHelper.wrapAndCall(options.onRowDoubleClick, e => {
            this.onGridRowDoubleClick(e)
        })

        if (options.type == null) {
            options.type = GridTypes.PAGINATED_SMALL
        }

        if (options.buttonsOptions == null) {
            options.buttonsOptions = { enabled: true }
        }

        if (options.selection == null) {
            options.selection = {}
        }

        if (this.isMultiSelect) {
            options.selection.mode = 'multiple'

            if (options.selection.showCheckBoxesMode == null) {
                options.selection.showCheckBoxesMode = 'never'
            }
        }
        else {
            options.selection.mode = 'single'
        }

        return options
    }

    private getSelectedObjects(): TObject[] {
        if (this.gridController != null) {
            return this.gridController.selectedItems
        }

        if (this.treeListController != null) {
            return this.treeListController.getSelectedRowsData()
        }

        return []
    }

    private onGridRowDoubleClick(e: any): void {
        this.selectObject()
    }

    private selectObject(): void {
        this.onObjectsSelected(this.getSelectedObjects())

        if (this.isClearSelectionAfterSelect) {
            if (this.getGrid() != null) {
                this.getGrid().dxGrid.clearSelection()
            }
            else if (this.getTreeList() != null) {
                this.getTreeList().clearSelection()
            }
        }
    }

    //#endregion


    //#region Grid

    private onGridInitialized(e: any, gridController: GridController<TObject>): void {
        this.gridController = gridController;

        // Podpinanie onSelectionChanged tutaj bo jakiś jełop ustawił półsekundowy timeout na evencie w GridOptions
        e.component.on('selectionChanged', (e: any) => {
            this.updateSubmitButton(this.gridController.selectedItems)
            this.onSelectionChanged(this.gridController.selectedItems)
        })
    }


    private initializeGrid(): JQueryPromise<void> {
        this.gridOptions = this.overwriteTableOptions(this.gridOptions)
        this.gridOptions.onInitialized = TypeHelper.wrapAndCall(this.gridOptions.onInitialized, (e: any, grid: GridController<TObject>) => {
            this.onGridInitialized(e, grid)
        })

        return super.waitForOnInitialized(this.gridOptions)
    }

    //#endregion


    //#region Tree

    private onTreeInitialized(e: any): void {
        this.treeListController = e.component

        // Ta samo co wyżej
        e.component.on('onSelectionChanged', (e: any) => {
            this.onSelectionChanged(this.gridController.selectedItems)
            this.updateSubmitButton(this.treeListController.getSelectedRowsData())
        })
    }

    private initializeTree() {
        this.treeListOptions = this.overwriteTableOptions(this.treeListOptions)

        return super
            .waitForOnInitialized(this.treeListOptions)
            .then(e => {
                this.onTreeInitialized(e)
            })
    }

    //#endregion


    //#region Bottom Toolbar

    private initializeBottomToolbar(): JQueryPromise<any> {
        this.bottomToolbarOptions = {
            items: []
        }

        if (this.isShowSelectButton) {
            this.bottomToolbarOptions.items.push({
                location: 'after',
                widget: 'dxButton',
                options: {
                    disabled: this.isRequired ? true : false,
                    text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),

                    onInitialized: (e: any) => {
                        this.submitButton = e.component
                    },

                    onClick: () => {
                        this.selectObject()
                    }
                }
            })
        }

        this.isBottomToolbarVisible = _.size(this.bottomToolbarOptions.items) > 0

        return super.waitForOnInitialized(this.bottomToolbarOptions)
    }

    private updateSubmitButton(selectedItems: TObject[]) {
        if (this.isRequired) {
            this.submitButton.option('disabled', _.size(selectedItems) === 0)
        }
    }

    //#endregion
}


